import { useEffect, useState } from "react";

import { checkIfClient } from "shared/lib/helpers";
import {
  AccountDetailsStatusI,
  AccountDetailsSummaryI,
  AccountI,
  AccountUserListI,
  SchedulingConfigI,
} from "shared/lib/interfaces/account";

import { AccountExecutiveI, ContactDetailI } from "@/lib/types/accounts";
import { CampaignI } from "@/lib/types/campaign";
import { getAccountDetailsData } from "@/helpers/account-details";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import {
  AccountNoteI,
  GetAccountDetailsDataI,
} from "@/lib/types/account-details";
import { useFetchAccountDetails } from "@/api/routes/account/queries";
import { useEffectOnceWhen } from "shared/lib/hooks";
import { dispatchCustomEvent } from "@/helpers/events";
import { AccountReloadCompleteEventDataI } from "@/lib/types/custom-events";

export const useAccountDetails = (
  campaignId: string,
  accountId: string,
  prefetchedData?: GetAccountDetailsDataI
): any => {
  const [account, setAccount] = useState<AccountI | undefined>(
    prefetchedData?.account
  );

  const [accountSummary, setAccountSummary] = useState<
    AccountDetailsSummaryI | undefined
  >(prefetchedData?.accountSummary);

  const [contacts, setContacts] = useState<ContactDetailI[] | undefined>(
    prefetchedData?.contacts
  );

  const [accountExecutive, setAccountExecutive] = useState<
    AccountExecutiveI | undefined | null
  >(prefetchedData?.accountExecutive);

  const [accountStatus, setAccountStatus] = useState<
    AccountDetailsStatusI | undefined
  >(prefetchedData?.accountStatus);

  const [accountUserLists, setAccountUserLists] = useState<
    AccountUserListI[] | undefined
  >(prefetchedData?.accountUserLists);

  const [campaign, setCampaign] = useState<CampaignI | undefined>(
    prefetchedData?.campaign
  );

  const [accountNote, setAccountNote] = useState<AccountNoteI | undefined>(
    prefetchedData?.accountNote
  );

  const [scheduling, setScheduling] = useState<SchedulingConfigI | undefined>(
    prefetchedData?.scheduling
  );

  const [isInitialFetchComplete, setIsInitialFetchComplete] =
    useState<boolean>(false);
  const [isFetchingData, setIsFetchingData] = useState<boolean>();
  const [isErrorOccurred, setIsErrorOccurred] = useState<boolean>(false);

  /**
   * Declaring and re-exporting it here to:
   * 1) avoid specifying campaign and account
   *    IDs in every call of useFetchAccountDetails down in the component tree.
   * 2) support re-fetching of the query in the onAccountUpdate function down here.
   */
  const accountDetailsQuery = useFetchAccountDetails(campaignId, accountId);

  const accountDetailsApi = {
    ...accountDetailsQuery,
    // This customization is required, because other than just using useFetchAccountDetails
    // hook, we're also manually fetching query in getAccountDetailsData, which
    // doesn't switch isFetching flag of query to `true`. However, we rely on this
    // flag to show loading state in the UI.
    isFetching: accountDetailsQuery.isFetching || !!isFetchingData,
  };

  const updateAccountDetails = async () => {
    setIsFetchingData(true);
    setIsErrorOccurred(false);

    const data = await getAccountDetailsData(campaignId, accountId);

    setIsErrorOccurred(data.isError || !!data.isNotApprovedForCampaign);

    setCampaign(data?.campaign);
    setAccountStatus(data?.accountStatus);
    setAccountSummary(data?.accountSummary);

    setAccount(data?.account);
    setAccountExecutive(data?.accountExecutive);
    setAccountUserLists(data?.accountUserLists);
    setContacts(data?.contacts);
    setIsFetchingData(false);
    setAccountNote(data?.accountNote);
    setScheduling(data?.scheduling);
    return data;
  };

  const onAccountUpdate = async () => {
    if (checkIfClient()) {
      const data = await updateAccountDetails();

      dispatchCustomEvent({
        eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD_COMPLETE,
        data: {
          campaign: data?.campaign,
          account: data?.account,
        } as AccountReloadCompleteEventDataI,
      });
    }
  };

  useEffect(() => {
    if (!prefetchedData) {
      updateAccountDetails();
    }

    window.document.addEventListener(
      CUSTOM_EVENTS.ACCOUNT.RELOAD,
      onAccountUpdate
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.ACCOUNT.RELOAD,
        onAccountUpdate
      );
    };
  }, [prefetchedData]);

  useEffectOnceWhen(() => {
    setIsInitialFetchComplete(true);
  }, isFetchingData === false);

  return {
    isFetchingData,
    isInitialFetchComplete,
    isErrorOccurred,

    accountDetailsApi,
    campaign,
    account,
    accountSummary,
    contacts,
    accountStatus,
    accountExecutive,
    accountNote,
    accountUserLists,
    scheduling,

    onAccountUpdate,
  };
};
